import React, { useMemo, useState } from "react";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

function clampNumber(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}

function parseNum(v) {
  const s = String(v ?? "")
    .trim()
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

function calcMonthlyPayment(principal, annualRatePct, months) {
  const P = principal;
  const n = months;
  const r = annualRatePct / 100 / 12;

  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / n;

  const denom = 1 - Math.pow(1 + r, -n);
  return (P * r) / denom;
}

function buildAmortizationSchedule(principal, annualRatePct, months) {
  const P = principal;
  const n = months;
  const r = annualRatePct / 100 / 12;

  const m = calcMonthlyPayment(P, annualRatePct, n);
  let balance = P;

  const rows = [];
  for (let i = 1; i <= n; i++) {
    const interest = r === 0 ? 0 : balance * r;
    const principalPaid = m - interest;
    balance = Math.max(0, balance - principalPaid);

    rows.push({
      month: i,
      payment: m,
      interest,
      principal: principalPaid,
      balance,
    });
  }
  return { monthlyPayment: m, rows };
}

async function exportPdf({ inputs, outputs, scheduleRows, includeSchedule }) {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595.28, 841.89]); // A4
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const margin = 52;
  let y = 841.89 - margin;

  const draw = (text, opts = {}) => {
    const size = opts.size ?? 12;
    page.drawText(String(text), {
      x: opts.x ?? margin,
      y,
      size,
      font: opts.bold ? fontBold : font,
      color: opts.color ?? rgb(0, 0, 0),
    });
    y -= size + (opts.gap ?? 6);
  };

  draw("Calcul mensualité crédit immobilier", {
    bold: true,
    size: 18,
    gap: 12,
  });
  draw("Entrées", { bold: true, size: 13, gap: 6 });
  inputs.forEach((l) => draw("• " + l, { size: 11 }));

  y -= 6;
  draw("Résultats", { bold: true, size: 13, gap: 6 });
  outputs.forEach((l) => draw("• " + l, { size: 11 }));

  if (includeSchedule && scheduleRows?.length) {
    y -= 10;
    draw("Tableau d’amortissement (12 premiers mois)", {
      bold: true,
      size: 13,
      gap: 8,
    });

    const header = ["Mois", "Mensualité", "Intérêts", "Capital", "CRD"];
    const take = scheduleRows.slice(0, 12);
    const colX = [margin, 90, 200, 310, 420];

    header.forEach((h, idx) =>
      page.drawText(h, { x: colX[idx], y, size: 10, font: fontBold })
    );
    y -= 16;

    for (const row of take) {
      const vals = [
        row.month,
        formatEUR(row.payment),
        formatEUR(row.interest),
        formatEUR(row.principal),
        formatEUR(row.balance),
      ];
      vals.forEach((v, idx) =>
        page.drawText(String(v), { x: colX[idx], y, size: 9, font })
      );
      y -= 14;
      if (y < 80) break;
    }
  }

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "calcul-mensualite-credit.pdf";
  a.click();
  URL.revokeObjectURL(url);
}

export default function CalculMensualiteCredit() {
  const [amount, setAmount] = useState("200000");
  const [rate, setRate] = useState("3,8");
  const [years, setYears] = useState("20");
  const [insuranceMonthly, setInsuranceMonthly] = useState("0");
  const [includeSchedule, setIncludeSchedule] = useState(true);

  const computed = useMemo(() => {
    const principal = clampNumber(parseNum(amount), 0, 50_000_000);
    const annualRatePct = clampNumber(parseNum(rate), 0, 30);
    const y = clampNumber(parseNum(years), 0, 40);
    const months = Math.round(y * 12);
    const ins = clampNumber(parseNum(insuranceMonthly), 0, 100_000);

    const { monthlyPayment, rows } = buildAmortizationSchedule(
      principal,
      annualRatePct,
      months
    );

    const monthlyTotal = monthlyPayment + ins;
    const totalPaidNoIns = monthlyPayment * months;
    const totalInterest = rows.reduce((acc, r) => acc + r.interest, 0);
    const totalIns = ins * months;
    const totalPaidWithIns = totalPaidNoIns + totalIns;

    return {
      principal,
      annualRatePct,
      years: y,
      months,
      insuranceMonthly: ins,
      monthlyPayment,
      monthlyTotal,
      totalPaidNoIns,
      totalPaidWithIns,
      totalInterest,
      totalIns,
      schedule: rows,
    };
  }, [amount, rate, years, insuranceMonthly]);

  const canCompute = computed.principal > 0 && computed.months > 0;

  return (
    <div className="page">
      <div className="hero">
        <h1>Calcul mensualité crédit</h1>
        <p className="muted" style={{ margin: 0 }}>
          Mensualité, coût total, intérêts + tableau d’amortissement. Calcul
          100% local.
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* FORM */}
        <div className="card">
          <h2 className="cardTitle">Entrées</h2>

          <label className="label">
            Montant emprunté (€)
            <input
              className="input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <label className="label">
            Taux annuel (%)
            <input
              className="input"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <label className="label">
            Durée (années)
            <input
              className="input"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              inputMode="numeric"
            />
          </label>

          <label className="label">
            Assurance mensuelle (€) (option)
            <input
              className="input"
              value={insuranceMonthly}
              onChange={(e) => setInsuranceMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginTop: 6,
            }}
          >
            <input
              type="checkbox"
              checked={includeSchedule}
              onChange={(e) => setIncludeSchedule(e.target.checked)}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              Inclure l’amortissement (6 mois) dans le PDF
            </span>
          </label>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn"
              disabled={!canCompute}
              onClick={() =>
                exportPdf({
                  inputs: [
                    `Montant : ${formatEUR(computed.principal)}`,
                    `Taux : ${computed.annualRatePct} %`,
                    `Durée : ${computed.years} ans (${computed.months} mois)`,
                    `Assurance : ${formatEUR(computed.insuranceMonthly)}`,
                  ],
                  outputs: [
                    `Mensualité hors assurance : ${formatEUR(
                      computed.monthlyPayment
                    )}`,
                    `Mensualité avec assurance : ${formatEUR(
                      computed.monthlyTotal
                    )}`,
                    `Coût des intérêts : ${formatEUR(computed.totalInterest)}`,
                    `Coût total (hors assurance) : ${formatEUR(
                      computed.totalPaidNoIns
                    )}`,
                    `Coût total (avec assurance) : ${formatEUR(
                      computed.totalPaidWithIns
                    )}`,
                  ],
                  scheduleRows: computed.schedule,
                  includeSchedule,
                })
              }
            >
              Télécharger PDF
            </button>

            <button
              className="btn btnSecondary"
              onClick={() => {
                setAmount("200000");
                setRate("3,8");
                setYears("20");
                setInsuranceMonthly("0");
                setIncludeSchedule(true);
              }}
            >
              Réinitialiser
            </button>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Estimation indicative. Certaines banques intègrent des paramètres
            supplémentaires (frais, différé, modulation…).
          </div>
        </div>

        {/* RESULTS */}
        <div className="card">
          <h2 className="cardTitle">Résultats</h2>

          {!canCompute ? (
            <p className="muted">Renseigne un montant et une durée.</p>
          ) : (
            <>
              <Result
                label="Mensualité (hors assurance)"
                value={formatEUR(computed.monthlyPayment)}
              />
              <Result
                label="Mensualité (avec assurance)"
                value={formatEUR(computed.monthlyTotal)}
              />
              <Result
                label="Coût total des intérêts"
                value={formatEUR(computed.totalInterest)}
              />
              <Result
                label="Coût total (hors assurance)"
                value={formatEUR(computed.totalPaidNoIns)}
              />
              <Result
                label="Coût total assurance"
                value={formatEUR(computed.totalIns)}
              />
              <Result
                label="Coût total (avec assurance)"
                value={formatEUR(computed.totalPaidWithIns)}
              />

              <div style={{ height: 14 }} />

              <h3
                style={{
                  margin: "0 0 10px 0",
                  fontSize: 16,
                  letterSpacing: ".2px",
                }}
              >
                Amortissement (12 mois)
              </h3>

              <div className="tableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th>Mensualité</th>
                      <th>Intérêts</th>
                      <th>Capital</th>
                      <th>CRD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {computed.schedule.slice(0, 6).map((r) => (
                      <tr key={r.month}>
                        <td>{r.month}</td>
                        <td>{formatEUR(r.payment)}</td>
                        <td>{formatEUR(r.interest)}</td>
                        <td>{formatEUR(r.principal)}</td>
                        <td>{formatEUR(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="small" style={{ marginTop: 10 }}>
                Tableau affiché sur 12 mois pour rester lisible (tu peux
                l’étendre si tu veux).
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Result({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
